import { useState, useMemo, useEffect, useCallback } from "react";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useVendors } from "@/hooks/useVendors";
import { useServiceCategories } from "@/hooks/useServiceCategories";
import type { Vendor, CreateVendorRequest } from "@/types/vendors";
import { PhotoManager } from "@/components/ui/PhotoManager";
import { PhotoParentType, type PhotoResponse } from "@/types/photos";
import { usePhotos, useDeletePhoto, usePhotoUploadWorkflow } from "@/hooks/usePhotos";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Eye,
  Power,
  PowerOff,
  Edit,
  Plus,
  Store,
  AlertCircle
} from "lucide-react";

type VendorStatus = 'all' | 'active' | 'suspended';
type SortField = 'name';
type SortOrder = 'asc' | 'desc';

interface VendorFilters {
  search: string;
  status: VendorStatus;
  category: string;
}

// Form schema for adding a new vendor — matches CreateVendorRequest exactly
// (name, contactEmail, contactPhone; isActive is pinned true on submit).
const vendorFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  contactEmail: z.string().email("Invalid email address").min(1, "Email is required").max(255, "Email must be less than 255 characters"),
  contactPhone: z.string().max(50, "Phone must be less than 50 characters").optional()
});

// Vendor Logo Section Component
const VendorLogoSection = ({
  vendorId,
  isEditMode,
  onPhotoChange
}: {
  vendorId: string;
  isEditMode: boolean;
  onPhotoChange?: (current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => void;
}) => {
  const { photos } = usePhotos(PhotoParentType.Vendor, vendorId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Vendor Logo</CardTitle>
      </CardHeader>
      <CardContent>
        {isEditMode ? (
          <PhotoManager
            currentPhotos={photos}
            maxPhotos={1}
            maxFileSizeMB={5}
            onChange={(current, newFiles, deletedIds) => {
              onPhotoChange?.(current, newFiles, deletedIds);
            }}
          />
        ) : (
          photos.length > 0 ? (
            <img
              src={photos[0].url}
              alt="Vendor logo"
              className="w-32 h-32 object-cover rounded-lg"
            />
          ) : (
            <div className="w-32 h-32 bg-muted rounded-lg flex items-center justify-center">
              <Store className="w-12 h-12 text-muted-foreground" />
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    case "suspended": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

// VendorFilters Component
const VendorFiltersBar = ({
  filters,
  onFilterChange
}: {
  filters: VendorFilters;
  onFilterChange: (field: keyof VendorFilters, value: string) => void;
}) => {
  const { data: categories = [] } = useServiceCategories();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search vendors..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Status Filter */}
      <Select value={filters.status} onValueChange={(value) => onFilterChange('status', value as VendorStatus)}>
        <SelectTrigger>
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="suspended">Suspended</SelectItem>
        </SelectContent>
      </Select>

      {/* Category Filter — sourced from the real service categories */}
      <Select value={filters.category} onValueChange={(value) => onFilterChange('category', value)}>
        <SelectTrigger>
          <SelectValue placeholder="All Categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Categories</SelectItem>
          {categories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              {category.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Add Vendor Modal Component
const AddVendorModal = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { createVendor } = useVendors();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof vendorFormSchema>>({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: {
      name: "",
      contactEmail: "",
      contactPhone: ""
    }
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const onSubmit = async (data: z.infer<typeof vendorFormSchema>) => {
    try {
      const vendorData: CreateVendorRequest = {
        name: data.name,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone || undefined,
        isActive: true
      };

      await createVendor(vendorData);

      toast({
        title: "Vendor created",
        description: `${data.name} has been added successfully.`
      });

      form.reset();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create vendor. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Vendor</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter vendor name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="vendor@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+971501234567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default function Vendors() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { vendors, isLoading, error, updateVendor, isUpdating } = useVendors();
  const deletePhoto = useDeletePhoto();
  const { uploadPhotos } = usePhotoUploadWorkflow();
  const [filters, setFilters] = useState<VendorFilters>({
    search: "",
    status: "all",
    category: "all"
  });
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [vendorPhotoChanges, setVendorPhotoChanges] = useState<{
    current: PhotoResponse[];
    newFiles: File[];
    deletedIds: string[];
  }>({
    current: [],
    newFiles: [],
    deletedIds: []
  });
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [sortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Filter and sort vendors
  const filteredAndSortedVendors = useMemo(() => {
    if (!vendors) return [];

    const filtered = vendors.filter(vendor => {
      const matchesSearch = vendor.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = filters.status === 'all' ||
                           (vendor.isActive && filters.status === 'active') ||
                           (!vendor.isActive && filters.status === 'suspended');
      const matchesCategory = filters.category === 'all' ||
                             (vendor.categories?.some(c => c.id === filters.category) ?? false);

      return matchesSearch && matchesStatus && matchesCategory;
    });

    return filtered.sort((a, b) => {
      const aValue = a[sortField].toLowerCase();
      const bValue = b[sortField].toLowerCase();

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [vendors, filters, sortField, sortOrder]);

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVendors(filteredAndSortedVendors.map(vendor => vendor.id));
    } else {
      setSelectedVendors([]);
    }
  };

  const handleSelectVendor = (vendorId: string, checked: boolean) => {
    if (checked) {
      setSelectedVendors([...selectedVendors, vendorId]);
    } else {
      setSelectedVendors(selectedVendors.filter(id => id !== vendorId));
    }
  };

  const handleFilterChange = (field: keyof VendorFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const openVendorDrawer = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setDrawerOpen(true);
  };

  // Wire the power toggle to a real, VALID PUT /marketplace/vendors/{id}
  // carrying the full UpdateVendorRequest (Name + ContactEmail are [Required]).
  const toggleVendorStatus = (vendor: Vendor) => {
    updateVendor({
      id: vendor.id,
      name: vendor.name,
      contactEmail: vendor.contactEmail ?? "",
      contactPhone: vendor.contactPhone,
      isActive: !vendor.isActive
    });
    // Keep the open drawer in sync so its Switch reflects the new state immediately.
    if (selectedVendor?.id === vendor.id) {
      setSelectedVendor({ ...vendor, isActive: !vendor.isActive });
    }
  };

  const handleVendorPhotoChange = useCallback((current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => {
    setVendorPhotoChanges({ current, newFiles, deletedIds });
  }, []);

  const handleSaveVendor = async () => {
    if (!selectedVendor) return;

    try {
      // Handle photo deletions first
      if (vendorPhotoChanges.deletedIds.length > 0) {
        for (const photoId of vendorPhotoChanges.deletedIds) {
          await deletePhoto.mutateAsync(photoId);
        }
      }

      // Handle photo uploads
      if (vendorPhotoChanges.newFiles.length > 0) {
        await uploadPhotos(
          PhotoParentType.Vendor,
          selectedVendor.id,
          vendorPhotoChanges.newFiles
        );
      }

      // Update vendor data
      updateVendor({
        id: selectedVendor.id,
        name: selectedVendor.name,
        contactEmail: selectedVendor.contactEmail ?? "",
        contactPhone: selectedVendor.contactPhone,
        isActive: selectedVendor.isActive
      });

      // Invalidate photo queries
      queryClient.invalidateQueries({
        queryKey: ["/photos", PhotoParentType.Vendor, selectedVendor.id]
      });

      // Reset edit mode
      setIsEditMode(false);
      setVendorPhotoChanges({ current: [], newFiles: [], deletedIds: [] });

      toast({
        title: "Vendor updated",
        description: "Vendor has been updated successfully."
      });
    } catch (error) {
      console.error("Error updating vendor:", error);
      toast({
        title: "Error",
        description: "Failed to update vendor. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header
          title="Vendors"
          description="Manage and monitor all vendor partnerships"
        >
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Add New Vendor
          </Button>
        </Header>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading vendors...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header
          title="Vendors"
          description="Manage and monitor all vendor partnerships"
        >
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Add New Vendor
          </Button>
        </Header>
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading vendors</AlertTitle>
            <AlertDescription>
              Failed to load vendor data. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <Header
          title="Vendors"
          description="Manage and monitor all vendor partnerships"
        >
          <Button onClick={() => setAddVendorOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Vendor
          </Button>
        </Header>

        <div className="flex-1 overflow-auto p-6">

          {/* Filters */}
          <Card className="athlyn-card mb-6">
            <CardContent className="p-6">
              <VendorFiltersBar
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </CardContent>
          </Card>

          {/* Vendors Table */}
          {filteredAndSortedVendors.length > 0 ? (
            <Card className="athlyn-card">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedVendors.length === filteredAndSortedVendors.length}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all vendors"
                        />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted hover:bg-opacity-50 transition-colors"
                        onClick={handleSort}
                      >
                        Vendor Name
                      </TableHead>
                      <TableHead>Owning Client</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedVendors.map((vendor) => (
                      <TableRow
                        key={vendor.id}
                        className={`hover:bg-muted hover:bg-opacity-50 transition-colors cursor-pointer ${
                          selectedVendors.includes(vendor.id) ? 'bg-primary bg-opacity-10' : ''
                        }`}
                        onClick={() => openVendorDrawer(vendor)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedVendors.includes(vendor.id)}
                            onCheckedChange={(checked) => handleSelectVendor(vendor.id, !!checked)}
                            aria-label={`Select ${vendor.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {vendor.logo?.url ? (
                              <img
                                src={vendor.logo.url}
                                alt={vendor.name}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                                <Store className="w-5 h-5 text-primary" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-foreground">{vendor.name}</p>
                              <p className="text-sm text-muted-foreground">{vendor.contactEmail}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {vendor.organizationName ?? "Global"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${getStatusColor(vendor.isActive ? 'active' : 'suspended')} text-xs`}>
                            {vendor.isActive ? 'Active' : 'Suspended'}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openVendorDrawer(vendor)}
                              className="w-8 h-8 p-0"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-8 h-8 p-0"
                              disabled={isUpdating}
                              onClick={() => toggleVendorStatus(vendor)}
                              aria-label={vendor.isActive ? `Disable ${vendor.name}` : `Enable ${vendor.name}`}
                            >
                              {vendor.isActive ?
                                <PowerOff className="w-4 h-4 text-red-500" /> :
                                <Power className="w-4 h-4 text-green-500" />
                              }
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            // Empty State
            <Card className="athlyn-card">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Store className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No vendors found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or add a new vendor to get started.
                </p>
                <Button
                  className="flex items-center gap-2 athlyn-button-primary"
                  onClick={() => setAddVendorOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add New Vendor
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Vendor Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedVendor && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                      <Store className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <SheetTitle>{selectedVendor.name}</SheetTitle>
                      <p className="text-sm text-muted-foreground">
                        {selectedVendor.organizationName ?? "Global"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={selectedVendor.isActive}
                    disabled={isUpdating}
                    onCheckedChange={() => toggleVendorStatus(selectedVendor)}
                    aria-label={selectedVendor.isActive ? "Disable vendor" : "Enable vendor"}
                  />
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-4">
                {/* Vendor Logo */}
                <VendorLogoSection
                  vendorId={selectedVendor.id}
                  isEditMode={isEditMode}
                  onPhotoChange={handleVendorPhotoChange}
                />

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedVendor.contactEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedVendor.contactPhone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Owning Client</p>
                      <p className="font-medium">{selectedVendor.organizationName ?? "Global"}</p>
                    </div>
                  </CardContent>
                </Card>

                {selectedVendor.categories && selectedVendor.categories.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Categories</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedVendor.categories.map((category) => (
                          <Badge key={category.id} variant="secondary" className="text-xs">
                            {category.name}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <SheetFooter className="mt-6">
                {isEditMode ? (
                  <>
                    <Button variant="outline" onClick={() => {
                      setIsEditMode(false);
                      setVendorPhotoChanges({ current: [], newFiles: [], deletedIds: [] });
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveVendor} disabled={isUpdating || deletePhoto.isPending}>
                      {isUpdating || deletePhoto.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" className="flex items-center gap-2" onClick={() => setIsEditMode(true)}>
                    <Edit className="w-4 h-4" />
                    Edit Vendor
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Vendor Modal */}
      <AddVendorModal
        open={addVendorOpen}
        onClose={() => setAddVendorOpen(false)}
      />
    </>
  );
}
